/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import getConfig from '../../config';
import uuid from 'uuid';

declare let adobeIMS: AdobeIMS;

const ingestPropertiesToExclude = [
	'event.guid',
	'event.context_guid',
	'event.context_sequence',
	'event.user_guid',
	'event.visitor_guid',
	'event.mcid_guid',
	'event.build',
	'source.client_id',
	'env.fw.name',
	'env.fw.version',
	'env.com.name',
	'env.com.version',
	'env.svc.name',
	'env.svc.version',
	'env.api.name',
	'env.api.version',
];

class EventsService {
	/**
	 * This method sends an event to the events table
	 * @param {array} eventInfo
	 */
	reportEvents(ingestEventInfo: Array<IngestEvent>) {
		if (!window.cchome.util.optOutMachineLearning && Array.isArray(ingestEventInfo) && ingestEventInfo.length > 0) {
			const eventsRequestPayload: EventRequestPayload = [];
			let allowToReport = false;

			ingestEventInfo.forEach((ingestEvent) => {
				const eventsArray = ingestEvent.events;
				const codeVersion = '1.1';

				/* istanbul ignore else */
				if (Array.isArray(eventsArray) && eventsArray.length > 0) {
					eventsArray.forEach((event) => {
						// copy the event data
						const eventInfo = Object.assign({}, event.data);

						if (!allowToReport) {
							const eventUrl = eventInfo['event.url'] || '';
							const matchUrl = eventUrl && typeof eventUrl === 'string' && eventUrl.match(/^[^?#]*/);

							const urlPath = matchUrl && matchUrl.length > 0 && matchUrl[0].split('/');

							/* istanbul ignore else */
							if (urlPath) {
								// Only check for reporting if we detect known url paths with enabled feature flags
								switch (urlPath[3]) {
									case '':
										if (window.cchome.util.hasFeature('intents_server_tracking_home')) {
											allowToReport = true;
										}

										break;
									case 'learn':
										if (window.cchome.util.hasFeature('intents_server_tracking_learn')) {
											allowToReport = true;
										}

										break;
									case 'apps':
										if (window.cchome.util.hasFeature('intents_server_tracking_apps')) {
											allowToReport = true;
										}

										break;
									case 'search':
										if (window.cchome.util.hasFeature('intents_server_tracking_search')) {
											allowToReport = true;
										}

										break;
									default:
										allowToReport = true;
										break;
								}
							}
							else {
								allowToReport = true;
							}
						}

						// if referrer has access token, replace the event.referrer with the text 'login'
						if (
							eventInfo['event.referrer']
							&& typeof eventInfo['event.referrer'] === 'string'
							&& eventInfo['event.referrer'].indexOf('token') > -1
						) {
							eventInfo['event.referrer'] = 'login';
						}

						// add code version # to the body
						eventInfo['code.version'] = codeVersion;

						// remove the properties we don't care about.
						ingestPropertiesToExclude.forEach((property) => {
							// eslint-disable-next-line no-prototype-builtins
							if (eventInfo.hasOwnProperty(property)) {
								delete eventInfo[property];
							}
						});

						// make sure none of our properties are empty strings
						// since DynamoDB doesn't like those.
						for (const property in eventInfo) {
							if (eventInfo[property] === '') {
								delete eventInfo[property];
							}
						}

						// add this event to the event request payload array
						const eventRequest = {
							method: 'POST',
							body: {},
						};
						eventRequest.body = eventInfo;
						eventsRequestPayload.push(eventRequest);
					});
				}
			});

			// send the request to the server

			/* istanbul ignore else */
			if (allowToReport && eventsRequestPayload && eventsRequestPayload.length > 0) {
				this.performCreateEventRequest(eventsRequestPayload);
			}
		}
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */

	/**
	 * This method posts an event to the intents server
	 * @param {object} bodyData event body
	 */
	performCreateEventRequest(bodyData: Array<Object>): Promise<any> {
		return new Promise((resolve) => {
			const postData = JSON.stringify(bodyData);
			const options = {
				hostname: getConfig('INTENTS_SERVER'),
				path: '/v1/users/me/events/batch',
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'x-api-key': getConfig('CLIENT_ID'),
					authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					'x-request-id': uuid.v4(),
				},
				dataType: 'json',
				withCredentials: false,
			};
			const request = https.request(options, (res) => {
				if (res.statusCode && res.statusCode >= 300) {
					window.cchome.util.logger.error('Events Service', 'Problem with Events request', res);
					const eventsServiceError = new Error('Events error');
					resolve(eventsServiceError);
				}
				else {
					let data = '';
					// set the encoding to UTF8 to prevent decoding issues on certain browsers.
					// https://jira.corp.adobe.com/browse/CCH-4427
					res.setEncoding('utf8');
					res.on('data', chunk => (data += chunk));
					res.on('end', () => {
						// Success!
						try {
							resolve(JSON.parse(data));
						}
						catch (e: any) {
							window.cchome.util.logger.error(
								'Events Service',
								'Problem with parsing Events response',
								e.toString(),
							);
							const eventsServiceError = new Error('Events parse error');
							resolve(eventsServiceError);
						}
					});
				}
			});

			request.on('error', (err) => {
				// There was a connection error of some sort
				window.cchome.util.logger.error('Events Service', 'Problem with Events request', err.toString());
				const eventsServiceError = new Error('Events error');
				resolve(eventsServiceError);
			});
			request.write(postData);
			request.end();
		});
	}
}

export default EventsService;
